import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { ProjectSetupRiskService } from './project-setup-risk.service';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { State, process } from '@progress/kendo-data-query';
import { map } from 'rxjs/operators';
import { UIControlTyp } from 'src/app/core/models/UIControlTyp';
import { ProjectRisk } from 'src/app/core/models/ProjectRisk';

@Component({
  standalone: false,
  selector: 'fdb-project-setup-risk',
  templateUrl: './project-setup-risk.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./project-setup-risk.component.scss']
})
export class ProjectSetupRiskComponent implements OnChanges {
  @Input() projectId = 0;
  public UIControlTyp = UIControlTyp;
  public view: Observable<GridDataResult>;

  public gridState: State = {
    sort: [],
    skip: 0,
    take: 10
  };
  editedRowIndex: any;
  editedRow: any;

  constructor(private formBuilder: UntypedFormBuilder, public componentService: ProjectSetupRiskService) {
    this.componentService.reset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.projectId.currentValue > 0) {
      this.initValues();
    }
  }

  public initValues() {
    this.view = this.componentService
      .pipe(
        map(data =>
          process(data, this.gridState)
        )
      );
    this.componentService.read(true, this.projectId);
  }

  public removeHandler({ sender, dataItem }) {
    this.componentService.remove(dataItem);
    this.componentService.saveChanges();
    sender.cancelCell();
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.editedRow = undefined;
  }

  public onStateChange(state: State) {
    this.gridState = state;
    this.componentService.read(true, this.projectId);
  }

  public editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.editedRowIndex = rowIndex;
    this.editedRow = Object.assign({}, dataItem);

    sender.editRow(rowIndex, this.createFormGroup(dataItem));
  }

  public addHandler({ sender }) {
    const newItem = new ProjectRisk();
    newItem.projectId = this.projectId;
    newItem.riskRatingId = 0;
    sender.addRow(this.createFormGroup(newItem));
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler({ sender, formGroup, rowIndex, isNew }) {
    if (formGroup.valid) {
      if (isNew) {
        this.componentService.create(formGroup.value);
      } else {
        let value = formGroup.value;
        value.id = sender.data.data[0].id;
        value.impact = sender.data.data[0].impact;
        value.name = sender.data.data[0].name;
        value.riskRatingId = sender.data.data[0].riskRatingId;
        this.componentService.assignValues(this.editedRow, value);
        this.componentService.update(this.editedRow);
      }
      this.closeEditor(sender, rowIndex);
      this.componentService.saveChanges();
    }
  }

  public createFormGroup(dataItem: ProjectRisk): UntypedFormGroup {
    return this.formBuilder.group({
      id: [dataItem.id, Validators.required],
      name: [dataItem.name, Validators.required],
      projectId: [dataItem.projectId, Validators.required],
      impact: dataItem.impact,
      riskRatingId: [dataItem.riskRatingId, Validators.required]
    });
  }

  public checkName(name: string, id: number): boolean {
    return this.componentService.hasName(name, id);
  }
}
